import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';

/**
 * @Injectable()
 * @description
 * Service responsible for generating URLs for the xFarm static map proxy endpoint.
 * This endpoint handles signing the Google Maps Static API URL internally,
 * centralizing usage control and access logging.
 */
@Injectable({
  providedIn: 'root', // Consider providing in 'root' if it's a singleton service
})
export class StaticMapProxyService {
  private apiBaseUrl = environment.apiBaseUrl;
  CDN = environment.publicAssetsUrl + 'assets/images/';

  // Check if static maps is disabled in this environment
  private staticMapsEnabled = environment.showStaticMap;

  // services
  private http = inject(HttpClient);

  constructor() {}

  /**
   * Retrieves a static map image URL from the xFarm proxy endpoint.
   * The endpoint handles signing the URL and redirects to Google's Static Maps API.
   *
   * @param imageWidth The desired width of the image in pixels.
   * @param imageHeight The desired height of the image in pixels.
   * @param mapType The type of map to display (e.g., 'roadmap', 'satellite', 'hybrid').
   * @param path A string defining a polygonal path to draw on the map,
   * e.g., 'color:0x35a235|weight:3|fillcolor:0x35a235|lat,lng|lat,lng'.
   * This parameter will be URL encoded by HttpParams.
   * @returns An Observable that emits the final redirect URL (Google Maps Static API URL).
   */
  getStaticMap(
    imageWidth: number,
    imageHeight: number,
    mapType: string,
    path: string
  ): Observable<string> {
    // Construct HttpParams to properly URL encode all parameters.
    // The HttpParams class automatically handles basic URL encoding.
    // For characters like '|', ':', ',', it will correctly encode them.
    const params = new HttpParams()
      .set('size', `${imageWidth}x${imageHeight}`)
      .set('maptype', mapType)
      .set('path', path); // HttpParams will handle encoding of special characters in 'path'

    // The endpoint returns an HTTP 308 Permanent Redirect with the signed Google Maps URL.
    // HttpClient by default follows redirects, so the Observable will emit the final URL.
    // We are expecting a string (the URL) as the response.
    return this.http.get(`${this.apiBaseUrl}api/public/maps/staticmap`, {
      params,
      responseType: 'text', // Specify responseType as 'text' since we expect a URL string
    });
  }

  /**
   * Creates a URL string for the xFarm static map proxy endpoint.
   * This URL can then be used to request a static map image. The proxy endpoint
   * will handle the Google Maps API key and URL signing, returning a 308 redirect
   * to the final Google Maps Static API image.
   *
   * @param features An array of GeoJSON-like feature objects. Each feature should
   * have a `geometry` property with `coordinates`. This function
   * supports 'Polygon', 'MultiPolygon', and 'LineString' geometry types.
   * For polygons, only the outer ring is used for the path.
   * @param colorByType The hexadecimal color string (e.g., '0x35a235') to use for the path
   * line and fill color. This value should be a raw string;
   * `HttpParams` will handle its URL encoding. Defaults to '0x35a235'.
   * @param size The desired width and height of the square map image in pixels.
   * Defaults to 250. The resulting image will be `size`x`size`.
   * @returns A complete URL string (e.g., `https://api-prod.xfarm.ag/api/private/maps/staticmap?...`)
   * that points to the xFarm proxy endpoint with all parameters properly encoded.
   */

  // --- BACKWARD-COMPATIBLE getStaticMapUrl ---

  /**
   * Builds a Static Maps proxy URL for the given GeoJSON features.
   *
   * Supported geometry types:
   *  - Polygon / MultiPolygon       → outer ring only (closed), uses fillcolor
   *  - LineString / MultiLineString → open polylines, no fillcolor
   *
   * Notes:
   *  - Uses Google encoded polyline (`enc:`) to keep URLs short and avoid 400/414.
   *  - Adaptive thinning is applied only for very dense shapes (override with { thinStep }):
   *      ≤ 400 pts: none | 401–800: step 2 | 801–1200: step 3 | > 1200: step 4
   *  - GeoJSON coordinates are [lng, lat]; converted to (lat, lng) before encoding.
   *  - Backward compatible with legacy signatures via overloads:
   *      getStaticMapUrl(features)
   *      getStaticMapUrl(features, '0xFF0000')
   *      getStaticMapUrl(features, undefined, 100)
   *      getStaticMapUrl(features, { size, maptype, color, weight, fillcolor, thinStep })
   */
  getStaticMapUrl(features: any[]): string;
  getStaticMapUrl(
    features: any[],
    opts: {
      size?: string;
      maptype?: string;
      color?: string;
      weight?: number;
      fillcolor?: string;
      thinStep?: number;
    }
  ): string;
  getStaticMapUrl(features: any[], color: string): string;
  getStaticMapUrl(features: any[], color: string | undefined, weight: number): string;

  getStaticMapUrl(features: any[], arg2?: any, arg3?: any): string {
    // Normalize options across the supported overloads
    let opts: {
      size?: string;
      maptype?: string;
      color?: string;
      weight?: number;
      fillcolor?: string;
      thinStep?: number;
    } = {};

    if (typeof arg2 === 'string') {
      // Legacy signature: (features, color) or (features, color, weight)
      opts.color = arg2;
    } else if (typeof arg2 === 'object' && arg2 !== null) {
      // New signature: (features, opts)
      opts = { ...arg2 };
    }
    if (typeof arg3 === 'number') {
      // Legacy third argument = weight
      opts.weight = arg3;
    }

    // Safe defaults (thinStep helps avoid 400 on very detailed polygons)
    const o = {
      size: '250x250',
      maptype: 'hybrid',
      color: '0x35a235',
      weight: 3,
      fillcolor: '0x35a235',
      thinStep: undefined as number | undefined, // no thinning by default
      ...opts,
    };

    // Check if static maps are enabled in the environment configuration.
    if (!this.staticMapsEnabled) {
      // If static maps are disabled, generate an inline SVG placeholder
      return this.buildGeoJsonSvgPlaceholder(features);
    }

    // Collect segments from supported geometry types (GeoJSON uses [lng, lat])
    type Seg = { points: Array<[number, number]>; isPolygon: boolean };
    const segments: Seg[] = [];

    for (const f of features ?? []) {
      const g = f?.geometry;
      if (!g) continue;

      if (g.type === 'Polygon') {
        if (g.coordinates?.[0]) segments.push({ points: g.coordinates[0], isPolygon: true });
      } else if (g.type === 'MultiPolygon') {
        for (const poly of g.coordinates ?? []) {
          if (poly?.[0]) segments.push({ points: poly[0], isPolygon: true });
        }
      } else if (g.type === 'LineString') {
        if (g.coordinates?.length) segments.push({ points: g.coordinates, isPolygon: false });
      } else if (g.type === 'MultiLineString') {
        for (const line of g.coordinates ?? []) {
          if (line?.length) segments.push({ points: line, isPolygon: false });
        }
      }
    }

    if (!segments.length) return `${this.CDN}empty.jpg`;

    // Base URL
    const base = `${environment.apiBaseUrl}api/public/maps/staticmap`;
    let url = `${base}?size=${encodeURIComponent(o.size)}&maptype=${encodeURIComponent(o.maptype)}`;

    // For each segment: convert, thin (adaptively), close (only polygons), encode, append &path=
    for (const seg of segments) {
      let latLng: Array<[number, number]> = seg.points.map(([lng, lat]) => [lat, lng]);

      const N = latLng.length;
      let step = 1;
      if (typeof o.thinStep === 'number' && o.thinStep > 1) {
        step = o.thinStep;
      } else {
        if (N > 1200) step = 4;
        else if (N > 800) step = 3;
        else if (N > 400) step = 2;
      }
      if (step > 1) latLng = this.thinPoints(latLng, step);

      if (seg.isPolygon) {
        const [fLat, fLng] = latLng[0];
        const [lLat, lLng] = latLng[latLng.length - 1];
        if (fLat !== lLat || fLng !== lLng) latLng.push([fLat, fLng]);
      }

      const enc = this.encodePolyline(latLng);
      const pathStyle = seg.isPolygon
        ? `color:${o.color}|weight:${o.weight}|fillcolor:${o.fillcolor}|enc:${enc}`
        : `color:${o.color}|weight:${o.weight}|enc:${enc}`;

      try {
        url += `&path=${encodeURIComponent(pathStyle)}`;
      } catch (e) {
        console.error('Invalid path encoding for field:', seg, e);
        return this.CDN + 'empty.jpg';
      }
    }

    return url;
  }

  getStaticMapUrlFromPoint(
    lat: number,
    lng: number,
    opts?: {
      size?: string;
      maptype?: string;
    }
  ): string {
    const o = {
      size: '250x250',
      maptype: 'hybrid',
      ...opts,
    };

    if (!this.staticMapsEnabled) {
      return `${this.CDN}empty.jpg`;
    }

    const base = `${environment.apiBaseUrl}api/public/maps/staticmap`;
    let url = `${base}?size=${encodeURIComponent(o.size)}&maptype=${encodeURIComponent(o.maptype)}`;

    const marker = `${lat},${lng}`;
    url += `&markers=${encodeURIComponent(marker)}`;

    return url;
  }

  /** Returns a thinned copy of points keeping the last vertex intact. */
  private thinPoints(points: Array<[number, number]>, step = 2): Array<[number, number]> {
    if (!Array.isArray(points) || step <= 1) return points;

    const out: Array<[number, number]> = [];
    for (let i = 0; i < points.length; i += step) out.push(points[i]);

    // ensure last vertex is kept (important for lines and ring closure checks)
    const last = points[points.length - 1];
    const tail = out[out.length - 1];
    if (!tail || tail[0] !== last[0] || tail[1] !== last[1]) out.push(last);

    return out;
  }

  private encodePolyline(latlngs: Array<[number, number]>) {
    let lastLat = 0,
      lastLng = 0,
      result = '';
    for (const [lat, lng] of latlngs) {
      const latE5 = Math.round(lat * 1e5);
      const lngE5 = Math.round(lng * 1e5);
      result += this.encodeSignedNumber(latE5 - lastLat);
      result += this.encodeSignedNumber(lngE5 - lastLng);
      lastLat = latE5;
      lastLng = lngE5;
    }
    return result;
  }

  private encodeSignedNumber(num: number) {
    let s = num < 0 ? ~(num << 1) : num << 1;
    let out = '';
    while (s >= 0x20) {
      out += String.fromCharCode((0x20 | (s & 0x1f)) + 63);
      s >>= 5;
    }
    out += String.fromCharCode(s + 63);
    return out;
  }

  /**
   * Genera un thumbnail SVG placeholder dai features GeoJSON.
   * Restituisce un data:image/svg+xml.
   */
  private buildGeoJsonSvgPlaceholder(features: any[], size = 300): string {
    if (!features || features.length === 0) {
      return `https://placehold.co/${size}?text=No+geometry&font=roboto`;
    }

    const allPoints: [number, number][] = [];
    const paths: string[] = [];

    features.forEach((f: any) => {
      if (!f.geometry) return;
      const coords = f.geometry.coordinates;

      const collect = (points: any[]) => {
        points.forEach((p: any) => {
          if (Array.isArray(p[0])) {
            collect(p);
          } else {
            allPoints.push([p[0], p[1]]);
          }
        });
      };
      collect(coords);
    });

    if (allPoints.length === 0) {
      return `https://placehold.co/${size}?text=No+geometry&font=roboto`;
    }

    const lons = allPoints.map((p) => p[0]);
    const lats = allPoints.map((p) => p[1]);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    const padding = size * 0.15;
    const usableW = size - padding * 2;
    const usableH = size - padding * 2;

    const scaleX = usableW / (maxLon - minLon || 1);
    const scaleY = usableH / (maxLat - minLat || 1);

    const project = (lon: number, lat: number) => {
      const x = padding + (lon - minLon) * scaleX;
      const y = size - padding - (lat - minLat) * scaleY; // invert y
      return [x.toFixed(2), y.toFixed(2)];
    };

    features.forEach((f: any) => {
      if (!f.geometry) return;
      const { type } = f.geometry;

      if (type === 'Polygon') {
        const ring = f.geometry.coordinates[0];
        const d = ring.map((pt: number[]) => project(pt[0], pt[1]).join(',')).join(' L ');
        paths.push(
          `<path d="M ${d} Z" fill="rgba(53,162,53,0.25)" stroke="rgba(53,162,53,0.9)" stroke-width="1"/>`
        );
      } else if (type === 'MultiPolygon') {
        f.geometry.coordinates.forEach((poly: any) => {
          const ring = poly[0];
          const d = ring.map((pt: number[]) => project(pt[0], pt[1]).join(',')).join(' L ');
          paths.push(
            `<path d="M ${d} Z" fill="rgba(53,162,53,0.25)" stroke="rgba(53,162,53,0.9)" stroke-width="1"/>`
          );
        });
      } else if (type === 'LineString') {
        const d = f.geometry.coordinates
          .map((pt: number[]) => project(pt[0], pt[1]).join(','))
          .join(' L ');
        paths.push(
          `<path d="M ${d}" fill="none" stroke="rgba(53,162,53,0.9)" stroke-width="1.5"/>`
        );
      }
    });

    const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <rect width="100%" height="100%" fill="#f2f4f7" rx="6" ry="6"/>
      ${paths.join('\n')}
    </svg>
  `.trim();

    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }
}
